// import node module libraries
import { Fragment, useState } from 'react';
import { Col, Row, Card, Button } from 'react-bootstrap';

// import custom components
import UsersListItems from './UserListItems';
import { useQuery } from '@apollo/client/react';
import { GET_ALL_USERS } from 'helper/graphql/queries';
import { Link } from 'react-router-dom';
import { PATH_ADMIN } from 'routes/Paths';
import { UserPlus } from 'react-feather';

const Users = () => {
	const [allUsers, setUsers] = useState([]);

	useQuery(
		GET_ALL_USERS,
		{
			onCompleted: (data) => {
				// order users by createdAt date descending
				const _users = data?.allUsers?.edges?.map(item => item.node).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
				setUsers(_users);
			}
		}
	);

	// Calculate stats
	const adminCount = allUsers.filter(u => u.role?.name?.toLowerCase() === 'administrator').length;
	const instructorCount = allUsers.filter(u => u.role?.name?.toLowerCase() === 'instructor').length;
	// const userCount = allUsers.filter(u => !u.role || u.role?.name?.toLowerCase() === 'user').length;
	const activeCount = allUsers.filter(u => u.isActive).length;

	return (
		<Fragment>
			{/* Page Header */}
			<div className="users-page-header mb-4">
				<Row className="align-items-center">
					<Col lg={8} md={12}>
						<div className="header-content">
							{/* <h1 className="page-title mb-2">
								<i className="fe fe-users me-3"></i>
								User Management
							</h1> */}
							<p className="page-description mb-0">
								Manage all users, instructors, and administrators on the platform
							</p>
						</div>
					</Col>
					<Col lg={4} md={12} className="text-lg-end mt-3 mt-lg-0">
						<Button 
							as={Link} 
							to={PATH_ADMIN.admin.addUsers} 
							variant="primary"
							className="add-user-btn"
						>
							<UserPlus size={18} className="me-2" />
							Add New User
						</Button>
					</Col>
				</Row>
			</div>

			{/* Stats Cards */}
			<Row className="g-3 mb-4">
				<Col lg={4} md={6} sm={12}>
					<Card className="stats-card-users stats-total">
						<Card.Body className="p-4">
							<div className="d-flex align-items-center justify-content-between">
								<div>
									<div className="stats-label">Total Users</div>
									<div className="stats-value">{allUsers.length}</div>
								</div>
								<div className="stats-icon-wrapper stats-icon-total">
									<i className="fe fe-users"></i>
								</div>
							</div>
						</Card.Body>
					</Card>
				</Col>
				<Col lg={4} md={6} sm={12}>
					<Card className="stats-card-users stats-admins">
						<Card.Body className="p-4">
							<div className="d-flex align-items-center justify-content-between">
								<div>
									<div className="stats-label">Administrators</div>
									<div className="stats-value">{adminCount}</div>
								</div>
								<div className="stats-icon-wrapper stats-icon-admins">
									<i className="fe fe-shield"></i>
								</div>
							</div>
						</Card.Body>
					</Card>
				</Col>
				<Col lg={4} md={6} sm={12}>
					<Card className="stats-card-users stats-instructors">
						<Card.Body className="p-4">
							<div className="d-flex align-items-center justify-content-between">
								<div>
									<div className="stats-label">Instructors</div>
									<div className="stats-value">{instructorCount}</div>
								</div>
								<div className="stats-icon-wrapper stats-icon-instructors">
									<i className="fe fe-award"></i>
								</div>
							</div>
						</Card.Body>
					</Card>
				</Col>
				{/* <Col lg={3} md={6} sm={12}>
					<Card className="stats-card-users stats-students">
						<Card.Body className="p-4">
							<div className="d-flex align-items-center justify-content-between">
								<div>
									<div className="stats-label">Students</div>
									<div className="stats-value">{userCount}</div>
								</div>
								<div className="stats-icon-wrapper stats-icon-students">
									<i className="fe fe-book-open"></i>
								</div>
							</div>
						</Card.Body>
					</Card>
				</Col> */}
			</Row>

			{/* Users Table */}
			<Card className="users-table-card">
				<Card.Header className="users-table-header">
					<div className="d-flex justify-content-between align-items-center">
						<div>
							<h5 className="mb-1">All Users</h5>
							<p className="text-muted mb-0" style={{fontSize: '0.875rem'}}>
								{activeCount} active • {allUsers.length - activeCount} inactive
							</p>
						</div>
					</div>
				</Card.Header>
				<Card.Body className="p-0">
					<UsersListItems users={allUsers} />
				</Card.Body>
			</Card>

			<style jsx>{`
				/* Page Header */
				.users-page-header {
					background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
					padding: 2rem;
					border-radius: 16px;
					border-left: 4px solid #1d9eb0;
				}

				.header-content {
					/* Handled by children */
				}

				.page-title {
					font-size: 2rem;
					font-weight: 800;
					color: #1e293b;
					display: flex;
					align-items: center;
				}

				.page-description {
					font-size: 1rem;
					color: #64748b;
				}

				:global(.add-user-btn) {
					padding: 0.75rem 1.5rem;
					font-weight: 600;
					border-radius: 10px;
					background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
					border: none;
					display: flex;
					align-items: center;
					transition: all 0.3s ease;
				}

				:global(.add-user-btn:hover) {
					transform: translateY(-2px);
					box-shadow: 0 8px 16px rgba(246, 133, 33, 0.3);
				}

				/* Stats Cards */
				:global(.stats-card-users) {
					border: none;
					border-radius: 12px;
					transition: all 0.3s ease;
					position: relative;
					overflow: hidden;
				}

				:global(.stats-card-users::before) {
					content: '';
					position: absolute;
					top: 0;
					left: 0;
					width: 4px;
					height: 100%;
					background: currentColor;
				}

				:global(.stats-card-users:hover) {
					transform: translateY(-4px);
					box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
				}

				:global(.stats-total::before) {
					background: #1d9eb0;
				}

				:global(.stats-admins::before) {
					background: #8b5cf6;
				}

				:global(.stats-instructors::before) {
					background: #F68521;
				}

				:global(.stats-students::before) {
					background: #10b981;
				}

				.stats-label {
					font-size: 0.8125rem;
					color: #94a3b8;
					font-weight: 600;
					text-transform: uppercase;
					letter-spacing: 0.5px;
					margin-bottom: 0.5rem;
				}

				.stats-value {
					font-size: 2rem;
					font-weight: 800;
					color: #1e293b;
				}

				.stats-icon-wrapper {
					width: 56px;
					height: 56px;
					display: flex;
					align-items: center;
					justify-content: center;
					border-radius: 12px;
					font-size: 1.75rem;
				}

				.stats-icon-total {
					background: rgba(29, 158, 176, 0.1);
					color: #1d9eb0;
				}

				.stats-icon-admins {
					background: rgba(139, 92, 246, 0.1);
					color: #8b5cf6;
				}

				.stats-icon-instructors {
					background: rgba(246, 133, 33, 0.1);
					color: #F68521;
				}

				.stats-icon-students {
					background: rgba(16, 185, 129, 0.1);
					color: #10b981;
				}

				/* Users Table Card */
				:global(.users-table-card) {
					border: none;
					border-radius: 16px;
					box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
					overflow: hidden;
				}

				:global(.users-table-header) {
					background: white;
					border-bottom: 2px solid #f1f5f9;
					padding: 1.5rem;
				}

				:global(.users-table-header) h5 {
					font-size: 1.125rem;
					font-weight: 700;
					color: #1e293b;
					margin: 0;
				}

				/* Responsive */
				@media (max-width: 991px) {
					.page-title {
						font-size: 1.5rem;
					}

					.users-page-header {
						padding: 1.5rem;
					}

					:global(.add-user-btn) {
						width: 100%;
						justify-content: center;
					}
				}

				@media (max-width: 768px) {
					.stats-value {
						font-size: 1.75rem;
					}
				}
			`}</style>
		</Fragment>
	);
};

export default Users;